import { useAuthState } from '@/app/providers/auth'
import { useItemNotesRemovedByFlag } from '@/components/page-specific/catering/common/helpers/useItemNotesRemovedByFlag'
import {
  AddressErrors,
  DateErrors,
  TimeErrors,
} from '@/components/page-specific/catering/common/types/enums/Errors.enum'
import { translateDateError } from '@/components/page-specific/catering/common/types/enums/translateDateError'
import { useCateringTeamOrderEnabled } from '@/helpers/features/cateringTeamOrder'
import { numberFormat } from '@/helpers/numbers'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { useWindowSize } from '@/hooks/useWindowSize'
import { getCurrencySymbol } from '@caterdesk/utils--money'
import {
  Alert,
  AlertTitle,
  Button,
  Drawer,
  Box as MuiBox,
  Snackbar,
  Stack,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material'
import type { AlertProps } from '@mui/material'

import { useParams } from 'next/navigation'
import type React from 'react'
import { useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { match, P } from 'ts-pattern'

import { useAppErrors } from '../../../common/store/appStateStore'
import type { MenuVendor } from '../../domain/menu-vendor'
import {
  useCartUI,
  useCartUIActions,
  useCartSnackbar,
  useCartSnackbarActions,
  useCartModals,
  useCartModalActions,
  useCartReorder,
} from '../../store/cartStore'
import CartToolbar from '../cart-toolbar'
import { buildParticipantList } from '../catering-team-order/avatar'
import { CateringTeamOrderContributorBanner } from '../catering-team-order/contributor-banner'
import { ParticipantPaletteProvider } from '../catering-team-order/participant-palette'
import { ParticipantStrip } from '../catering-team-order/participant-strip'
import ConsolidatedBasket from '../consolidated-basket'
import BasketFooter from '../consolidated-basket/basket-footer'
import ConsolidatedBasketHeader from '../consolidated-basket/consolidated-header'
import ErrorSection from '../consolidated-basket/error-section'
import OrderFooter from '../consolidated-basket/order-footer'
import StandingOrderFooter from '../consolidated-basket/standing-order-footer'
import InputBar from '../input-bar'
import { MenuItemModal } from '../menu-item-card/menu-item-modal'
import NewDraftButton from '../new-draft-button'
import ReorderBasket from '../reorder-basket'
import { ReorderBasketFooter } from '../reorder-basket/basket-footer'
import { ReorderBasketHeader } from '../reorder-basket/basket-header'
import { ReorderBasketInputBar } from '../reorder-basket/input-bar'
import { ReorderDrawer } from '../reorder-drawer'
import { useShoppingCart } from './useShoppingCart'

const ConsolidatedBasketWrapper: React.FC<
  PropsWithChildren<{ isMobile: boolean; open: boolean; onClose: () => void }>
> = ({ children, isMobile, open, onClose }) => {
  return isMobile ? (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: '100vw',
          backgroundColor: alpha('#E4E4E4', 0.9),
          padding: 2,
          top: '64px', // Height of the navigation bar
          height: 'calc(100vh - 64px)', // Full height minus nav bar
          zIndex: (theme) => theme.zIndex.drawer - 1, // Below navigation
        },
      }}>
      {children}
    </Drawer>
  ) : (
    children
  )
}

interface CartProps {
  vendor: MenuVendor
}

export const ShoppingCart: React.FC<CartProps> = ({ vendor }) => {
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()
  const { height: verticalHeight } = useWindowSize()
  const appErrors = useAppErrors()
  const params = useParams()
  const orderIdParam = params?.orderId
  const standingOrderIdParam = params?.standingOrderId

  const showMultipleBaskets = useFeatureFlag(SPLITS.MULTIPLE_BASKETS_GM_MARKETPLACE)
  const isReorderV2 = useFeatureFlag(SPLITS.CATERING_REORDER_MENU_PAGE)
  const cateringTeamOrderEnabled = useCateringTeamOrderEnabled()

  const { mobileBasketOpen, navHeight } = useCartUI()
  const { setMobileBasketOpen } = useCartUIActions()
  const { type: snackbarType } = useCartSnackbar()
  const { loadState: reorderLoadState } = useCartReorder()
  const isReorderLoading = reorderLoadState === 'loading'
  const { hideSnackbar } = useCartSnackbarActions()
  const { orderDetailsModal, editItemModal } = useCartModals()
  const { setOrderDetailsModalState, setEditItemModal } = useCartModalActions()

  const orderDetailsModalState = orderDetailsModal

  const authState = useAuthState()
  const userId = authState.type === 'authenticated' ? authState.user.id : undefined

  const {
    orderSelectedSettings,
    editingStandingOrderTemplate,
    mappedBasketorOrderDataToCartData,
    loadingBasketDrawerItems,
    showNewBasketActions,
    isDataUpdating,
    vendorUpdatedItems,
    basketMinimumOrderReached,
    basketMinSpendRemaining,
    selectedBasket,
    unselectedBaskets,
    basketData,
    unavailableBasketItems,
    isDeliveryDatePast,
    taxAcronym,
    existingOrderState,
    basketState,
    isEditingExistingOrder,
    isEditingStandingOrder,
    isCateringTeamOrderBasket,
    isCateringTeamOrderContributor,
    cateringTeamOrderContext,
    handleCreateBasket,
    handleOrderDetailsModalOpenChange,
    handleSaveBasketName,
    handleUserBasketSettingsConfirm,
    checkoutCallback,
    handleDeleteItem,
    handleQuantityChange,
    handleNoteChange,
    handleNumberedOptionQuantityChange,
    handleCopyFriendlyId,
    handleVendorUpdatedItemsKeep,
    handleVendorUpdatedItemsRemove,
    handleRemoveUnavailableItems,
    handleEditItemConfirm,
    deleteBasket,
    setSelectedBasket,
    updateUserState,
  } = useShoppingCart({
    vendor,
  })

  // existing-order editing has no note-update mutation (separate
  // GraphQL path for a GmOrder, not UpdateBasketItemInput), so the boolean is
  // switched off there even when the feature-flag is on. notesRemovedByFlag
  // mirrors the exact check the menu item modal uses, so the basket can't
  // disagree with it.
  const notesRemovedByFlag = useItemNotesRemovedByFlag()
  const showItemNotes =
    useFeatureFlag(SPLITS.USE_ITEM_NOTES_IN_BASKET) &&
    !isEditingExistingOrder &&
    !notesRemovedByFlag

  const activeColorVariant = isCateringTeamOrderBasket ? 'secondary' : 'primary'

  // A placed order carries no contributor roster, so in edit mode it is rebuilt from the
  // owner plus the attributed lines (including lines added locally, not yet saved).
  const editedOrder = existingOrderState.orderData
  const editedOrderItems = existingOrderState.orderItems
  const groupRoster = useMemo(
    () =>
      isEditingExistingOrder
        ? {
            ownerUserId: editedOrder?.user ? Number(editedOrder.user.id) : null,
            ownerName: editedOrder?.user?.fullName ?? '',
            contributors: editedOrderItems.flatMap((line) =>
              line.addedByUserId != null
                ? [{ userId: line.addedByUserId, name: line.addedByName ?? '' }]
                : [],
            ),
          }
        : {
            ownerUserId: basketData?.userId ?? null,
            ownerName: [basketData?.user?.firstName, basketData?.user?.lastName]
              .filter(Boolean)
              .join(' '),
            contributors: basketData?.contributors ?? [],
          },
    [isEditingExistingOrder, editedOrder, editedOrderItems, basketData],
  )
  const groupParticipants = useMemo(
    () =>
      buildParticipantList(
        groupRoster.ownerUserId,
        groupRoster.ownerName,
        groupRoster.contributors,
      ),
    [groupRoster],
  )

  const contributorLockedTooltip = isCateringTeamOrderContributor
    ? t('marketplace:cateringTeamOrderOwnerOnly', {
        name:
          cateringTeamOrderContext?.ordererName || t('marketplace:cateringTeamOrderOwnerFallback'),
      })
    : undefined

  const [contributorBannerHeight, setContributorBannerHeight] = useState(0)
  const unselectedBasketsHeight = showMultipleBaskets ? 45 * unselectedBaskets.length : 0
  const newDraftButtonHeight = 20
  const extraPaddingHeight = 45
  const basketMaxHeight =
    verticalHeight -
    (!isMobileScreen ? navHeight : 0) -
    (!isMobileScreen ? unselectedBasketsHeight : 0) -
    extraPaddingHeight -
    (showNewBasketActions ? newDraftButtonHeight : 0) -
    contributorBannerHeight

  useEffect(() => {
    if (orderIdParam || standingOrderIdParam || editingStandingOrderTemplate) return
    if (!basketData && !selectedBasket) return
    if (orderDetailsModalState.open) return
    if (
      appErrors.time === TimeErrors.TIME_PAST ||
      appErrors.date === DateErrors.DATE_PAST ||
      appErrors.date === DateErrors.INVALID_DATE ||
      appErrors.time === TimeErrors.INVALID_TIME ||
      appErrors.address === AddressErrors.VENDER_COVERAGE
    ) {
      setOrderDetailsModalState({ open: true })
    }
  }, [
    orderDetailsModalState.open,
    appErrors,
    basketData,
    orderIdParam,
    selectedBasket,
    standingOrderIdParam,
    editingStandingOrderTemplate,
    setOrderDetailsModalState,
  ])

  return (
    <MuiBox>
      <ConsolidatedBasketWrapper
        isMobile={isMobileScreen}
        open={mobileBasketOpen}
        onClose={() => setMobileBasketOpen(false)}>
        <Stack direction="column" paddingTop={1}>
          {showNewBasketActions &&
            (cateringTeamOrderEnabled ? (
              <CartToolbar
                onNewDraft={() => handleCreateBasket()}
                onNewCateringTeamOrder={() => handleCreateBasket({ isCateringTeamOrder: true })}
                disabled={loadingBasketDrawerItems}
                showCateringTeamOrder={
                  authState.type === 'authenticated' &&
                  !isEditingExistingOrder &&
                  !isEditingStandingOrder
                }
                confirmationText={
                  !showMultipleBaskets && basketData
                    ? t('marketplace:yourCurrentDraftWill')
                    : undefined
                }
              />
            ) : (
              <NewDraftButton
                onClick={handleCreateBasket}
                disabled={loadingBasketDrawerItems}
                confirmationText={
                  !showMultipleBaskets && basketData
                    ? t('marketplace:yourCurrentDraftWill')
                    : undefined
                }
              />
            ))}

          <Stack direction="column">
            {showMultipleBaskets &&
            !isEditingExistingOrder &&
            !isEditingStandingOrder &&
            !isCateringTeamOrderContributor
              ? unselectedBaskets.map((basketOption) => {
                  return (
                    <MuiBox
                      onClick={() => {
                        setSelectedBasket(basketOption.id)
                      }}
                      key={basketOption.id}
                      sx={{
                        cursor: 'pointer',
                        overflow: 'hidden',
                        paddingBottom: 0.5,
                      }}>
                      {isReorderV2 ? (
                        <ReorderBasketHeader
                          variant="inactive"
                          colorVariant={
                            cateringTeamOrderEnabled && basketOption.isCateringTeamOrder
                              ? 'secondary'
                              : 'primary'
                          }
                          friendlyId={basketOption.friendlyId ?? ''}
                          // Use || not ?? so empty/whitespace names (not just
                          // null) fall back to "Draft order" — unnamed drafts
                          // can arrive with customName === ''.
                          basketHeaderName={
                            basketOption.customName?.trim() ||
                            (cateringTeamOrderEnabled && basketOption.isCateringTeamOrder
                              ? t('marketplace:cateringTeamOrder')
                              : t('marketplace:draftOrder'))
                          }
                          copyFriendlyId={() => handleCopyFriendlyId(basketOption.id)}
                          onDelete={() => deleteBasket(basketOption.id)}
                        />
                      ) : (
                        <ConsolidatedBasketHeader
                          variant="inactive"
                          friendlyId={basketOption.friendlyId ?? ''}
                          copyFriendlyId={() => handleCopyFriendlyId(basketOption.id)}
                          // disableDelete={basketSelectionState.loading}
                          onDelete={() => deleteBasket(basketOption.id)}
                          // Use || not ?? so empty/whitespace names fall back too.
                          basketHeaderName={
                            basketOption.customName?.trim() || t('marketplace:draftOrder')
                          }
                        />
                      )}
                    </MuiBox>
                  )
                })
              : null}
            {cateringTeamOrderContext && (
              <CateringTeamOrderContributorBanner
                ordererName={cateringTeamOrderContext.ordererName}
                deliveryDateIso={cateringTeamOrderContext.deliveryDateIso}
                deliveryTime={cateringTeamOrderContext.deliveryTime}
                locationLabel={cateringTeamOrderContext.locationLabel}
                onHeightChange={setContributorBannerHeight}
              />
            )}
            {isReorderV2 ? (
              // Colours for every avatar in this basket are dealt out here, above both the
              // participant strip and the per-line attribution rows, so no two people in the
              // same basket share one.
              <ParticipantPaletteProvider participants={groupParticipants}>
                <ReorderBasket
                  maxHeight={`${basketMaxHeight}px`}
                  id={mappedBasketorOrderDataToCartData.id}
                  loadingNewData={loadingBasketDrawerItems}
                  items={mappedBasketorOrderDataToCartData.items}
                  fees={mappedBasketorOrderDataToCartData.fees}
                  taxRates={mappedBasketorOrderDataToCartData.taxRates}
                  summary={mappedBasketorOrderDataToCartData.summary}
                  taxAcronym={taxAcronym || 'TAX'}
                  dataIsUpdating={isDataUpdating}
                  onDeleteClick={handleDeleteItem}
                  onQuantityChange={handleQuantityChange}
                  onNoteChange={handleNoteChange}
                  showItemNotes={showItemNotes}
                  onNumberedOptionQuantityChange={handleNumberedOptionQuantityChange}
                  unavailableItemIds={unavailableBasketItems.map((item) => item.itemReference)}
                  hasActiveBasket={
                    Boolean(basketData) || isEditingExistingOrder || isEditingStandingOrder
                  }
                  colorVariant={activeColorVariant}
                  participantSection={
                    isCateringTeamOrderBasket && (basketData || isEditingExistingOrder) ? (
                      <ParticipantStrip {...groupRoster} currentUserId={userId} />
                    ) : null
                  }
                  headerSection={
                    <ReorderBasketHeader
                      variant="active"
                      colorVariant={activeColorVariant}
                      disableActions={isReorderLoading}
                      lockedTooltip={contributorLockedTooltip}
                      // Group baskets always show Share Link — sharing an empty group basket
                      // is the primary flow (the owner shares before anyone adds items).
                      hideCopy={
                        mappedBasketorOrderDataToCartData.items.length < 1 &&
                        !isCateringTeamOrderBasket
                      }
                      friendlyId={
                        mappedBasketorOrderDataToCartData.summary.friendlyId ||
                        selectedBasket?.friendlyId ||
                        ''
                      }
                      basketHeaderName={match({
                        isEditingExistingOrder,
                        isEditingStandingOrder,
                      })
                        .with(
                          { isEditingExistingOrder: true },
                          () =>
                            existingOrderState.orderData?.customName ?? t('marketplace:editOrder'),
                        )
                        .with({ isEditingStandingOrder: true }, () =>
                          t('marketplace:standingOrder'),
                        )
                        .otherwise(
                          () =>
                            basketData?.customName?.trim() ||
                            (isCateringTeamOrderBasket
                              ? t('marketplace:cateringTeamOrder')
                              : t('marketplace:draftOrder')),
                        )}
                      copyFriendlyId={() => handleCopyFriendlyId()}
                      onClose={isMobileScreen ? () => setMobileBasketOpen(false) : undefined}
                      onDelete={match(selectedBasket)
                        .with({ id: P.string }, (basket) => () => deleteBasket(basket.id))
                        .otherwise(() => undefined)}
                      onNameChange={match({
                        isEditingExistingOrder,
                        isEditingStandingOrder,
                        showMultipleBaskets,
                        basketData,
                      })
                        .with(
                          {
                            isEditingExistingOrder: false,
                            isEditingStandingOrder: false,
                            showMultipleBaskets: true,
                            basketData: P.not(P.nullish),
                          },
                          () => (newName: string) => {
                            handleSaveBasketName(newName)
                          },
                        )
                        .otherwise(() => undefined)}
                    />
                  }
                  reorderSection={
                    // PT-2155: reorder and team ordering are kept as separate flows.
                    !isEditingExistingOrder &&
                    !isEditingStandingOrder &&
                    !isCateringTeamOrderContributor &&
                    !isCateringTeamOrderBasket ? (
                      <ReorderDrawer
                        userId={userId}
                        vendor={vendor}
                        activeBasket={basketData ?? null}
                        orderSettings={orderSelectedSettings}
                        currencySymbol={getCurrencySymbol(vendor.currency)}
                        taxAcronym={taxAcronym || 'TAX'}
                        isStandingOrderTemplate={
                          isEditingStandingOrder || Boolean(editingStandingOrderTemplate)
                        }
                        onRequestCreateBasket={handleCreateBasket}
                      />
                    ) : null
                  }
                  inputBarSection={
                    showMultipleBaskets ? (
                      <>
                        {/* Contributors can't edit these and the banner above already shows them */}
                        {!isCateringTeamOrderContributor && (
                          <ReorderBasketInputBar
                            orderSelectedSettings={orderSelectedSettings}
                            disabled={isEditingExistingOrder || editingStandingOrderTemplate}
                            hideDate={Boolean(editingStandingOrderTemplate)}
                            dateHasError={isDeliveryDatePast}
                            onEditClick={() => setOrderDetailsModalState({ open: true })}
                          />
                        )}
                        {/*
                        V1's InputBar hosts the edit dialog. We hide its embedded
                        field UI (V2 above renders the visual fields), but keep
                        the component mounted so its Dialog still opens via the
                        shared modalOpen flag. Replace with a standalone dialog
                        component when the V1 path is removed.
                      */}
                        <MuiBox sx={{ display: 'none' }} aria-hidden>
                          <InputBar
                            loading={loadingBasketDrawerItems}
                            showBasketEmbeddedVersion
                            disabled={
                              isEditingExistingOrder ||
                              editingStandingOrderTemplate ||
                              isCateringTeamOrderContributor
                            }
                            orderSelectedSettings={orderSelectedSettings}
                            hideFields={{ date: editingStandingOrderTemplate }}
                            onValueChange={(values) => updateUserState(values)}
                            vendorId={vendor.id}
                            modalOpen={orderDetailsModalState.open}
                            modalIsLoading={isDataUpdating}
                            setModalOpen={handleOrderDetailsModalOpenChange}
                            onConfirm={(newState) => {
                              handleUserBasketSettingsConfirm(newState)
                            }}
                          />
                        </MuiBox>
                      </>
                    ) : null
                  }
                  errorSection={
                    <ErrorSection
                      severity={match<
                        {
                          orderSaveError: boolean
                          itemsUnavailable: boolean
                          pastEditCutoffError: boolean
                        },
                        AlertProps['severity']
                      >({
                        orderSaveError: Boolean(existingOrderState.saveError),
                        itemsUnavailable: unavailableBasketItems.length > 0,
                        pastEditCutoffError: Boolean(
                          existingOrderState.validationErrors.PAST_EDIT_CUT_OFF,
                        ),
                      })
                        .with({ orderSaveError: true }, () => 'error')
                        .with({ itemsUnavailable: true }, () => 'warning')
                        .with({ pastEditCutoffError: true }, () => 'error')
                        .otherwise(() => 'warning')}
                      isUpdating={isDataUpdating}
                      error={match({
                        minSpendNotMet: isEditingExistingOrder
                          ? Boolean(existingOrderState.validationErrors.MINIMUM_SPEND_NOT_MET)
                          : !basketMinimumOrderReached,
                        itemsEditedByVendor: vendorUpdatedItems.length > 0,
                        itemsUnavailable: unavailableBasketItems.length > 0,
                        orderSaveError: Boolean(existingOrderState.saveError),
                        pastEditCutoffError: Boolean(
                          existingOrderState.validationErrors.PAST_EDIT_CUT_OFF,
                        ),
                        invalidPaymentMethod: Boolean(
                          existingOrderState.validationErrors.INVALID_PAYMENT_METHOD,
                        ),
                      })
                        .with({ itemsUnavailable: true }, () => (
                          <>
                            {t('marketplace:itemsUnavailableForSelectedDate')}{' '}
                            <Button color="warning" onClick={handleRemoveUnavailableItems}>
                              {t('marketplace:remove')}
                            </Button>
                          </>
                        ))
                        .with({ minSpendNotMet: true }, () => {
                          const minimumSpendLeft =
                            existingOrderState.validationErrors.MINIMUM_SPEND_NOT_MET
                              ?.amountRequired ?? numberFormat(basketMinSpendRemaining / 100)

                          return (
                            <>
                              {t('marketplace:youNeedToSpend', {
                                currency: getCurrencySymbol(vendor.currency),
                                minimumSpendLeft,
                                taxAcronym,
                              })}
                            </>
                          )
                        })
                        .with({ invalidPaymentMethod: true }, () => (
                          <>{t('marketplace:ordersPaidForBy')}</>
                        ))
                        .with({ pastEditCutoffError: true }, () => (
                          <>{t('marketplace:changesCanNoLonger')}</>
                        ))
                        .with({ orderSaveError: true }, () => (
                          <>
                            {t('marketplace:unableToSaveChanges')}:{' '}
                            {existingOrderState.saveError?.message}
                          </>
                        ))
                        .with({ itemsEditedByVendor: true }, () => (
                          <>
                            {t('marketplace:someItemsHaveBeen')}
                            <Button color="warning" onClick={handleVendorUpdatedItemsKeep}>
                              {t('marketplace:keep')}
                            </Button>

                            <Button color="warning" onClick={handleVendorUpdatedItemsRemove}>
                              {t('marketplace:remove')}
                            </Button>
                          </>
                        ))
                        .otherwise(() => undefined)}
                    />
                  }
                  footerSection={match({
                    isEditingExistingOrder,
                    isEditingStandingOrder,
                  })
                    .with({ isEditingExistingOrder: true }, () => (
                      <OrderFooter
                        disableSubmission={!existingOrderState.canSaveChanges}
                        loading={existingOrderState.saving || isDataUpdating}
                        save={() => {
                          void existingOrderState.saveOrderChanges()
                        }}
                      />
                    ))
                    .with({ isEditingStandingOrder: true }, () => (
                      <StandingOrderFooter
                        disableSubmission={!basketMinimumOrderReached}
                        save={() => basketState.saveStandingOrderChanges()}
                        loading={isDataUpdating}
                        grandTotal={mappedBasketorOrderDataToCartData.summary.grandTotal}
                        taxAcronym={taxAcronym || 'TAX'}
                      />
                    ))
                    .otherwise(() => (
                      <ReorderBasketFooter
                        grandTotal={mappedBasketorOrderDataToCartData.summary.grandTotal}
                        taxAcronym={taxAcronym || 'TAX'}
                        onCheckout={() => (basketData ? checkoutCallback(basketData.id) : null)}
                        checkoutDisabled={
                          isCateringTeamOrderContributor ||
                          !basketMinimumOrderReached ||
                          unavailableBasketItems.length > 0 ||
                          Boolean(
                            appErrors.address ||
                            appErrors.date ||
                            appErrors.time ||
                            appErrors.headCount,
                          )
                        }
                        checkoutDisabledReason={
                          contributorLockedTooltip ??
                          (appErrors.date === DateErrors.DATE_PAST
                            ? translateDateError(t, 'DATE_PAST')
                            : undefined)
                        }
                        loading={isDataUpdating}
                      />
                    ))}
                />
              </ParticipantPaletteProvider>
            ) : (
              <ConsolidatedBasket
                maxHeight={`${basketMaxHeight}px`}
                id={mappedBasketorOrderDataToCartData.id}
                loadingNewData={loadingBasketDrawerItems}
                items={mappedBasketorOrderDataToCartData.items}
                fees={mappedBasketorOrderDataToCartData.fees}
                taxRates={mappedBasketorOrderDataToCartData.taxRates}
                summary={mappedBasketorOrderDataToCartData.summary}
                taxAcronym={taxAcronym || 'TAX'}
                dataIsUpdating={isDataUpdating}
                onDeleteClick={handleDeleteItem}
                onQuantityChange={handleQuantityChange}
                onNoteChange={handleNoteChange}
                showItemNotes={showItemNotes}
                onNumberedOptionQuantityChange={handleNumberedOptionQuantityChange}
                headerSection={
                  <ConsolidatedBasketHeader
                    onClose={isMobileScreen ? () => setMobileBasketOpen(false) : undefined}
                    onDelete={match(selectedBasket)
                      .with({ id: P.string }, (basket) => () => deleteBasket(basket.id))
                      .otherwise(() => undefined)}
                    friendlyId={
                      mappedBasketorOrderDataToCartData.summary.friendlyId ||
                      selectedBasket?.friendlyId ||
                      ''
                    }
                    copyFriendlyId={() => handleCopyFriendlyId()}
                    onNameChange={match({
                      isEditingExistingOrder,
                      isEditingStandingOrder,
                      showMultipleBaskets,
                      basketData,
                    })
                      .with(
                        {
                          isEditingExistingOrder: false,
                          isEditingStandingOrder: false,
                          showMultipleBaskets: true,
                          basketData: P.not(P.nullish),
                        },
                        () => (newName: string) => {
                          handleSaveBasketName(newName)
                        },
                      )
                      .otherwise(() => undefined)}
                    basketHeaderName={match({
                      isEditingExistingOrder,
                      isEditingStandingOrder,
                    })
                      .with(
                        { isEditingExistingOrder: true },
                        () =>
                          existingOrderState.orderData?.customName ?? t('marketplace:editOrder'),
                      )
                      .with({ isEditingStandingOrder: true }, () => t('marketplace:standingOrder'))
                      .otherwise(
                        () => basketData?.customName?.trim() || t('marketplace:draftOrder'),
                      )}
                  />
                }
                inputBarSection={
                  showMultipleBaskets ? (
                    <InputBar
                      loading={loadingBasketDrawerItems}
                      showBasketEmbeddedVersion
                      disabled={isEditingExistingOrder || editingStandingOrderTemplate}
                      orderSelectedSettings={orderSelectedSettings}
                      hideFields={{ date: editingStandingOrderTemplate }}
                      onValueChange={(values) => updateUserState(values)}
                      vendorId={vendor.id}
                      modalOpen={orderDetailsModalState.open}
                      modalIsLoading={isDataUpdating}
                      setModalOpen={handleOrderDetailsModalOpenChange}
                      onConfirm={(newState) => {
                        handleUserBasketSettingsConfirm(newState)
                      }}
                    />
                  ) : null
                }
                errorSection={
                  <ErrorSection
                    severity={match<
                      {
                        orderSaveError: boolean
                        itemsUnavailable: boolean
                        pastEditCutoffError: boolean
                      },
                      AlertProps['severity']
                    >({
                      orderSaveError: Boolean(existingOrderState.saveError),
                      itemsUnavailable: unavailableBasketItems.length > 0,
                      pastEditCutoffError: Boolean(
                        existingOrderState.validationErrors.PAST_EDIT_CUT_OFF,
                      ),
                    })
                      .with({ orderSaveError: true }, () => 'error')
                      .with({ itemsUnavailable: true }, () => 'warning')
                      .with({ pastEditCutoffError: true }, () => 'error')
                      .otherwise(() => 'warning')}
                    isUpdating={isDataUpdating}
                    error={match({
                      minSpendNotMet: isEditingExistingOrder
                        ? Boolean(existingOrderState.validationErrors.MINIMUM_SPEND_NOT_MET)
                        : !basketMinimumOrderReached,
                      itemsEditedByVendor: vendorUpdatedItems.length > 0,
                      itemsUnavailable: unavailableBasketItems.length > 0,
                      orderSaveError: Boolean(existingOrderState.saveError),
                      pastEditCutoffError: Boolean(
                        existingOrderState.validationErrors.PAST_EDIT_CUT_OFF,
                      ),
                      invalidPaymentMethod: Boolean(
                        existingOrderState.validationErrors.INVALID_PAYMENT_METHOD,
                      ),
                    })
                      .with({ itemsUnavailable: true }, () => (
                        <>
                          {t('marketplace:itemsUnavailableForSelectedDate')}{' '}
                          <Button color="warning" onClick={handleRemoveUnavailableItems}>
                            {t('marketplace:remove')}
                          </Button>
                        </>
                      ))
                      .with({ minSpendNotMet: true }, () => {
                        const minimumSpendLeft =
                          existingOrderState.validationErrors.MINIMUM_SPEND_NOT_MET
                            ?.amountRequired ?? numberFormat(basketMinSpendRemaining / 100)

                        return (
                          <>
                            {t('marketplace:youNeedToSpend', {
                              currency: getCurrencySymbol(vendor.currency),
                              minimumSpendLeft,
                              taxAcronym,
                            })}
                          </>
                        )
                      })
                      .with({ invalidPaymentMethod: true }, () => (
                        <>{t('marketplace:ordersPaidForBy')}</>
                      ))
                      .with({ pastEditCutoffError: true }, () => (
                        <>{t('marketplace:changesCanNoLonger')}</>
                      ))
                      .with({ orderSaveError: true }, () => (
                        <>
                          {t('marketplace:unableToSaveChanges')}:{' '}
                          {existingOrderState.saveError?.message}
                        </>
                      ))
                      .with({ itemsEditedByVendor: true }, () => (
                        <>
                          {t('marketplace:someItemsHaveBeen')}
                          <Button color="warning" onClick={handleVendorUpdatedItemsKeep}>
                            {t('marketplace:keep')}
                          </Button>

                          <Button color="warning" onClick={handleVendorUpdatedItemsRemove}>
                            {t('marketplace:remove')}
                          </Button>
                        </>
                      ))
                      .otherwise(() => undefined)}
                  />
                }
                footerSection={match({
                  isEditingExistingOrder,
                  isEditingStandingOrder,
                })
                  .with({ isEditingExistingOrder: true }, () => (
                    <OrderFooter
                      disableSubmission={!existingOrderState.canSaveChanges}
                      loading={existingOrderState.saving || isDataUpdating}
                      save={() => {
                        void existingOrderState.saveOrderChanges()
                      }}
                    />
                  ))
                  .with({ isEditingStandingOrder: true }, () => (
                    <StandingOrderFooter
                      disableSubmission={!basketMinimumOrderReached}
                      save={() => basketState.saveStandingOrderChanges()}
                      loading={isDataUpdating}
                    />
                  ))
                  .otherwise(() => (
                    <BasketFooter
                      disableSubmission={
                        !basketMinimumOrderReached ||
                        unavailableBasketItems.length > 0 ||
                        Boolean(
                          appErrors.address ||
                          appErrors.date ||
                          appErrors.time ||
                          appErrors.headCount,
                        )
                      }
                      checkout={() => (basketData ? checkoutCallback(basketData.id) : null)}
                      loading={isDataUpdating}
                    />
                  ))}
                unavailableItemIds={unavailableBasketItems.map((item) => item.itemReference)}
              />
            )}
          </Stack>
        </Stack>
      </ConsolidatedBasketWrapper>

      {(basketData || existingOrderState) && editItemModal.type === 'displayed' && (
        <>
          {editItemModal.mode === 'add' && (
            <MenuItemModal
              taxAcronym={taxAcronym || 'TAX'}
              mode="add"
              menuItem={editItemModal.menuItem}
              basketItem={editItemModal.basketItem}
              menuItemReference={editItemModal.menuItemReference}
              vendor={vendor}
              onConfirmClick={(payload) =>
                handleEditItemConfirm(payload as Parameters<typeof handleEditItemConfirm>[0])
              }
              onClose={() => setEditItemModal({ type: 'hidden' })}
            />
          )}
          {editItemModal.mode === 'edit' && (
            <MenuItemModal
              taxAcronym={taxAcronym || 'TAX'}
              mode="edit"
              menuItem={editItemModal.menuItem}
              basketItem={editItemModal.basketItem}
              menuItemReference={editItemModal.menuItemReference}
              vendor={vendor}
              onConfirmClick={(payload) => {
                // Type cast needed due to conflicting UpdateBasketItemPayload types
                handleEditItemConfirm(payload as Parameters<typeof handleEditItemConfirm>[0])
              }}
              onClose={() => setEditItemModal({ type: 'hidden' })}
            />
          )}
        </>
      )}

      <Snackbar
        open={Boolean(snackbarType)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoHideDuration={match(snackbarType)
          .with('linkCopied', () => 2000)
          .with('standingOrderSaved', () => 2000)
          .otherwise(() => undefined)}
        sx={{ marginBottom: '60px' }}
        onClose={(_e: React.SyntheticEvent | Event, reason?: string) => {
          if (reason === 'clickaway') {
            return
          }
          hideSnackbar()
        }}>
        <Stack>
          {match(snackbarType)
            .with('standingOrderSaved', () => (
              <Alert severity="success" variant="filled" sx={{ zIndex: 4 }}>
                <AlertTitle sx={{ marginBottom: 0 }}>
                  {t('marketplace:standingOrderSaved')}
                </AlertTitle>
              </Alert>
            ))
            .with('linkCopied', () => (
              <Alert severity="success" variant="filled" sx={{ zIndex: 4 }}>
                <AlertTitle sx={{ marginBottom: 0 }}>
                  {t('marketplace:linkCopiedToClipboard')}
                </AlertTitle>
              </Alert>
            ))
            .otherwise(() => null)}
        </Stack>
      </Snackbar>
    </MuiBox>
  )
}
